import { tr } from '@/lib/i18n-core';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Plus, RefreshCw, RotateCcw, SearchX } from 'lucide-react';
import type { BedBoardCell } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { visibleInterval } from '@/lib/polling';
import { useReducedMotion } from '@/lib/motion';
import { cn, display, timeOnly } from '@/lib/utils';
import { Dialog, EmptyState, PageHeader } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Skeleton, Tooltip, statusLabel } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import {
  BED_STATUS_CHOICES,
  EMPTY_BOARD_FILTER,
  bedSignature,
  bedStatusChangeReady,
  changedBeds,
  countDischargesDue,
  countStatuses,
  groupByWardAndRoom,
  matchesBoardFilter,
  releasesBedBeforeCleaningIsDone,
  wardCounts,
  type BoardFilter,
  type StatusCounts,
  type WardGroup,
} from './bed-status';
import { BedCard, type BedAbilities, type BedHandlers } from './BedCard';
import { BedBoardSummary } from './BedBoardSummary';
import { BoardFilterBar, WardChips, type WardChoice } from './BedBoardFilters';
import { BedTransferDialog } from './BedTransferDialog';
import { EmptyWardIllustration, StatusMiniBar } from './bed-board-art';
import { WardPhoneEditor } from './WardPhoneEditor';

const BOARD_REFRESH_LIVE_MS = 300_000;
const BOARD_REFRESH_OFFLINE_MS = 60_000;
const FLASH_MS = 2_400;
const BOARD_PATH = '/ipd/beds';
const NO_CELLS: BedBoardCell[] = [];
const NOTHING_CHANGED: ReadonlySet<string> = new Set();
const ROOM_WIDTH_CAP = 6;
const SKELETON_TILES = ['t1', 't2', 't3', 't4', 't5', 't6'];
const SKELETON_CHIPS = ['c1', 'c2', 'c3', 'c4'];
const SKELETON_CARDS = ['b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8'];
const CARD_GRID = 'grid grid-cols-[repeat(auto-fill,minmax(min(15rem,100%),1fr))] gap-3';

interface StatusRequest {
  cell: BedBoardCell;
  initial: string;
}

function useChangedBeds(cells: BedBoardCell[] | undefined): ReadonlySet<string> {
  const previous = useRef<Map<string, string> | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [flashing, setFlashing] = useState<ReadonlySet<string>>(NOTHING_CHANGED);

  useEffect(() => {
    if (!cells) return;
    const changed = changedBeds(previous.current, cells);
    previous.current = new Map(cells.map((cell) => [cell.bedId, bedSignature(cell)]));
    if (changed.length === 0) return;
    setFlashing(new Set(changed));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFlashing(NOTHING_CHANGED), FLASH_MS);
  }, [cells]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return flashing;
}

function roomFlex(bedCount: number): string {
  const beds = Math.min(Math.max(bedCount, 1), ROOM_WIDTH_CAP);
  return `0 1 calc(${beds} * 15rem + ${beds - 1} * 0.75rem + 1.5rem)`;
}

function LiveBadge({ live, updatedAt }: { live: boolean; updatedAt: number }) {
  return (
    <span className="flex items-center gap-2">
      <Tooltip content={live ? tr('ipd', 'bed_board_live_hint') : tr('ipd', 'bed_board_polling_hint')}>
        <span
          tabIndex={0}
          className={cn(
            'inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-medium focus-ring',
            live ? 'border-success/30 bg-success/10 text-success' : 'border-border bg-muted text-muted-foreground',
          )}
        >
          <span className="relative flex h-2 w-2" aria-hidden>
            {live && <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 motion-safe:animate-ping" />}
            <span className={cn('relative inline-flex h-2 w-2 rounded-full', live ? 'bg-success' : 'bg-muted-foreground')} />
          </span>
          {live ? tr('ipd', 'bed_board_live') : tr('ipd', 'bed_board_polling')}
        </span>
      </Tooltip>
      {updatedAt > 0 && (
        <span className="hidden text-xs text-muted-foreground lg:inline">
          {tr('ipd', 'updated_x', { time: timeOnly(new Date(updatedAt)) })}
        </span>
      )}
    </span>
  );
}

function BoardSkeleton() {
  return (
    <div role="status" aria-label={tr('ipd', 'loading')} className="space-y-5">
      <div className="grid items-center gap-5 rounded-2xl border bg-card p-5 lg:grid-cols-[auto_minmax(0,1fr)]">
        <Skeleton className="mx-auto h-40 w-40 rounded-full sm:h-44 sm:w-44" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-6">
          {SKELETON_TILES.map((key) => (
            <Skeleton key={key} className="h-[7.5rem] rounded-xl" />
          ))}
        </div>
      </div>
      <div className="flex gap-2 overflow-hidden">
        {SKELETON_CHIPS.map((key) => (
          <Skeleton key={key} className="h-16 w-40 shrink-0 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-24 rounded-xl" />
      <div className={CARD_GRID}>
        {SKELETON_CARDS.map((key) => (
          <Skeleton key={key} className="h-48 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function WardSection({
  ward,
  counts,
  rooms,
  children,
}: {
  ward: WardGroup;
  counts: StatusCounts;
  rooms: ReadonlyMap<string, { occupied: number; total: number }>;
  children: (room: WardGroup['rooms'][number]) => ReactNode;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="mb-8 duration-500 motion-safe:animate-in motion-safe:fade-in-0">
      <header className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 id={headingId} className="text-base font-semibold tracking-tight">
          {ward.wardName}
        </h2>
        <span className="text-xs text-muted-foreground tabular">
          {tr('ipd', 'x_of_y_occupied', { occupied: counts.occupied, total: counts.total })}
        </span>
        <StatusMiniBar counts={counts} className="w-32" />
      </header>
      <div className="flex flex-wrap gap-4">
        {ward.rooms.map((room) => {
          const totals = rooms.get(room.key);
          return (
            <div
              key={room.key}
              className="min-w-0 max-w-full rounded-2xl border border-dashed border-border bg-muted/20 p-2.5"
              style={{ flex: roomFlex(room.cells.length) }}
            >
              <p className="mb-2 flex items-center gap-1.5 px-1 text-xs font-semibold text-muted-foreground">
                <DoorOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">
                  {tr('ipd', 'room_x', { code: room.roomCode })}
                  {room.roomName && <span className="font-normal"> · {room.roomName}</span>}
                </span>
                {totals && (
                  <span className="ms-auto shrink-0 font-normal tabular" aria-label={tr('ipd', 'x_of_y_occupied', totals)}>
                    {totals.occupied}/{totals.total}
                  </span>
                )}
              </p>
              <div className={CARD_GRID}>{children(room)}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function roomTotals(cells: readonly BedBoardCell[]): Map<string, { occupied: number; total: number }> {
  const totals = new Map<string, { occupied: number; total: number }>();
  for (const cell of cells) {
    const key = `${cell.wardId}:${cell.roomCode}`;
    const entry = totals.get(key) ?? { occupied: 0, total: 0 };
    entry.total += 1;
    if (cell.status === 'occupied') entry.occupied += 1;
    totals.set(key, entry);
  }
  return totals;
}

function bedClassesOf(cells: readonly BedBoardCell[]): string[] {
  return [...new Set(cells.map((cell) => cell.bedClass))].sort();
}

function wardChoicesOf(cells: readonly BedBoardCell[]): WardChoice[] {
  const counts = wardCounts(cells);
  const names = new Map<string, string>();
  for (const cell of cells) if (!names.has(cell.wardId)) names.set(cell.wardId, cell.wardName);
  return [...names.entries()].map(([wardId, wardName]) => ({ wardId, wardName, counts: counts.get(wardId) ?? countStatuses([]) }));
}

/**
 * Live bed board (SRS 6.6).
 *
 * Laid out as beds rather than as a table, because the question the ward clerk
 * asks is spatial — "what is free in the general ward?" — and a list of forty
 * rows does not answer it at a glance. Occupied cells carry the balance, since
 * that is the other half of the discharge conversation.
 */
export function BedBoardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<BoardFilter>(EMPTY_BOARD_FILTER);
  const [statusFor, setStatusFor] = useState<StatusRequest | null>(null);
  const [transferFor, setTransferFor] = useState<BedBoardCell | null>(null);
  const reduced = useReducedMotion();
  const { can, hasModule } = useAuth();
  const mayManageBeds = can('bed.manage');

  const wards = useQuery({
    queryKey: ['wards'],
    queryFn: () => get<{ id: string; name: string; code: string; nurseStationPhone?: string | null }[]>('/ipd/wards'),
    enabled: mayManageBeds,
  });

  const live = useRealtimeInvalidate(['bed-board'], null, [['bed-board']]);
  const board = useQuery({
    queryKey: ['bed-board'],
    queryFn: () => get<BedBoardCell[]>('/ipd/bed-board'),
    refetchInterval: visibleInterval(live ? BOARD_REFRESH_LIVE_MS : BOARD_REFRESH_OFFLINE_MS),
  });

  const cells = board.data ?? NO_CELLS;
  const flashing = useChangedBeds(board.data);
  const timeZone = display.timezone;
  const now = useMemo(() => new Date(board.dataUpdatedAt || Date.now()), [board.dataUpdatedAt]);

  const wardChoices = useMemo(() => wardChoicesOf(cells), [cells]);
  const wardParam = params.get('ward') ?? '';
  const wardId = wardChoices.some((ward) => ward.wardId === wardParam) ? wardParam : '';
  const selectedWard = wards.data?.find((ward) => ward.id === wardId) ?? null;
  const returnTo = wardId ? `${BOARD_PATH}?ward=${encodeURIComponent(wardId)}` : BOARD_PATH;

  const view = useMemo(() => {
    const scope = wardId ? cells.filter((cell) => cell.wardId === wardId) : cells;
    const visible = scope.filter((cell) => matchesBoardFilter(cell, filter, now, timeZone));
    const groups = groupByWardAndRoom(visible);
    return {
      allCounts: countStatuses(cells),
      scopeCounts: countStatuses(scope),
      due: countDischargesDue(scope, now, timeZone),
      wardTotals: wardCounts(cells),
      rooms: roomTotals(scope),
      bedClasses: bedClassesOf(cells),
      visibleCount: visible.length,
      groups,
      stagger: new Map(groups.flatMap((ward) => ward.rooms.flatMap((room) => room.cells)).map((cell, index) => [cell.bedId, index])),
    };
  }, [cells, wardId, filter, now, timeZone]);

  const abilities: BedAbilities = {
    admit: can('admission.manage'),
    readAdmission: can('admission.read'),
    transfer: can('admission.manage', 'bed.manage'),
    discharge: can('admission.manage'),
    manageBeds: mayManageBeds,
    housekeeping: hasModule('housekeeping') && can('housekeeping.manage'),
    bill: can('invoice.read'),
  };

  const handlers: BedHandlers = {
    openAdmission: (cell) => navigate(`/ipd/admissions/${cell.admissionId}`),
    admit: () => navigate(`/ipd/admissions/new?returnTo=${encodeURIComponent(returnTo)}`),
    transfer: (cell) => setTransferFor(cell),
    discharge: (cell) => navigate(`/ipd/admissions/${cell.admissionId}/discharge?returnTo=${encodeURIComponent(returnTo)}`),
    bill: (cell) => navigate(`/billing/statement?admissionId=${cell.admissionId}`),
    setStatus: (cell, target) => setStatusFor({ cell, initial: target ?? cell.status }),
    housekeeping: () => navigate('/housekeeping'),
  };

  const chooseWard = (next: string) => setParams(next ? { ward: next } : {}, { replace: true });
  const resetFilters = () => setFilter(EMPTY_BOARD_FILTER);

  return (
    <>
      <PageHeader
        title={tr('ipd', 'bed_board')}
        description={tr('ipd', 'who_is_where_and_what_they_owe')}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <LiveBadge live={live} updatedAt={board.dataUpdatedAt} />
            <Tooltip content={tr('ipd', 'refresh_the_board')}>
              <Button
                variant="outline"
                size="icon"
                aria-label={tr('ipd', 'refresh_the_board')}
                onClick={() => void board.refetch()}
                disabled={board.isFetching}
              >
                <RefreshCw className={cn('h-4 w-4', board.isFetching && 'motion-safe:animate-spin')} aria-hidden />
              </Button>
            </Tooltip>
            {selectedWard && mayManageBeds && <WardPhoneEditor key={selectedWard.id} ward={selectedWard} />}
          </span>
        }
      />

      {board.isLoading && <BoardSkeleton />}
      {board.isError && (
        <Alert variant="destructive" className="mb-4">
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {tr('ipd', 'could_not_load_the_bed_board')} {describeError(board.error)}
            </span>
            <Button size="sm" variant="outline" onClick={() => void board.refetch()} loading={board.isFetching}>
              {tr('ipd', 'try_again')}
            </Button>
          </span>
        </Alert>
      )}
      {board.isSuccess && cells.length === 0 && (
        <EmptyState
          illustration={<EmptyWardIllustration />}
          title={tr('ipd', 'no_beds_to_show')}
          description={tr('ipd', 'beds_are_added_under_wards_and_rooms_in_administration')}
          action={
            mayManageBeds && (
              <Button onClick={() => navigate('/admin/wards/new')}>
                <Plus className="h-4 w-4" aria-hidden />
                {tr('ipd', 'add_a_ward')}
              </Button>
            )
          }
        />
      )}

      {cells.length > 0 && (
        <>
          <BedBoardSummary
            counts={view.scopeCounts}
            dischargesToday={view.due.today}
            dischargesTomorrow={view.due.tomorrow}
            statusFilter={filter.status}
            onStatusFilter={(status) => setFilter((current) => ({ ...current, status }))}
          />
          <WardChips wards={wardChoices} allCounts={view.allCounts} wardId={wardId} onChange={chooseWard} />
          <BoardFilterBar
            filter={filter}
            bedClasses={view.bedClasses}
            counts={view.scopeCounts}
            matching={view.visibleCount}
            onChange={setFilter}
            onReset={resetFilters}
          />

          {view.groups.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title={tr('ipd', 'no_beds_match_these_filters')}
              description={tr('ipd', 'try_another_ward_or_clear_the_filters')}
              action={
                <Button variant="outline" onClick={resetFilters}>
                  <RotateCcw className="h-4 w-4" aria-hidden />
                  {tr('ipd', 'clear_filters')}
                </Button>
              }
            />
          ) : (
            view.groups.map((ward) => (
              <WardSection key={ward.wardId} ward={ward} counts={view.wardTotals.get(ward.wardId) ?? countStatuses([])} rooms={view.rooms}>
                {(room) =>
                  room.cells.map((cell) => (
                    <BedCard
                      key={cell.bedId}
                      cell={cell}
                      now={now}
                      timeZone={timeZone}
                      index={view.stagger.get(cell.bedId) ?? 0}
                      flashing={flashing.has(cell.bedId)}
                      reduced={reduced}
                      can={abilities}
                      handlers={handlers}
                    />
                  ))
                }
              </WardSection>
            ))
          )}
        </>
      )}

      {statusFor !== null && (
        <BedStatusDialog cell={statusFor.cell} initialStatus={statusFor.initial} onClose={() => setStatusFor(null)} />
      )}
      {transferFor !== null && (
        <BedTransferDialog cell={transferFor} board={cells} onClose={() => setTransferFor(null)} />
      )}
    </>
  );
}

function BedStatusDialog({ cell, initialStatus, onClose }: { cell: BedBoardCell; initialStatus: string; onClose: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>(initialStatus);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mayManage = can('bed.manage');
  const reasonMatters = releasesBedBeforeCleaningIsDone(cell.status, status);

  const save = useMutation({
    mutationFn: () => put(`/ipd/beds/${cell.bedId}/status`, { status, reason: reason.trim() || undefined }),
    onSuccess: () => {
      toast.success(tr('ipd', 'bed_updated'));
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('ipd', 'bed_x', { bedCode: cell.bedCode })}
      description={tr('ipd', 'move_the_bed_between_housekeeping_states')}
      footer={
        mayManage && (
          <>
            <Button variant="ghost" onClick={onClose}>
              {tr('ipd', 'cancel')}
            </Button>
            <Button
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              loading={save.isPending}
              disabled={!bedStatusChangeReady({ currentStatus: cell.status, nextStatus: status, reason })}
            >
              {tr('ipd', 'update_bed')}
            </Button>
          </>
        )
      }
    >
      {mayManage ? (
        <div className="space-y-3">
          <Field label={tr('ipd', 'status')}>
            <ChoiceButtons
              layout="chips"
              value={status}
              onChange={setStatus}
              options={BED_STATUS_CHOICES.map((choice) => ({ value: choice, label: statusLabel(choice) }))}
            />
          </Field>
          <Field
            label={tr('ipd', 'reason')}
            required={reasonMatters}
            hint={
              reasonMatters
                ? tr('ipd', 'housekeeping_may_not_have_finished_this_bed')
                : tr('ipd', 'optional_kept_with_the_bed_for_example_why_it')
            }
          >
            <Input maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {error && <Alert variant="destructive">{error}</Alert>}
          <p className="text-xs text-muted-foreground">
            {tr('ipd', 'a_bed_becomes_occupied_by_admitting_or')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{tr('ipd', 'your_role_cannot_change_bed_status')}</p>
      )}
    </Dialog>
  );
}
